import { Component, ElementRef, HostListener, OnInit, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { AgentService } from '../../../services/agent.service';
import { DistrictService, DistrictDto } from '../../../services/district.service';

interface ZoneOption {
  value: string;
  label: string;
  districtId: string | null;
}

interface DistrictGroup {
  id: string | null;
  name: string;
  zones: ZoneOption[];
  expanded: boolean;
}

const UNASSIGNED_GROUP_ID = null;

/**
 * Self-rendered zone picker grouped by district, with search across both district and zone
 * names. Emits the flat list of selected zone IDs — a drop-in for a plain multi-select zone
 * dropdown via [(ngModel)].
 */
@Component({
  selector: 'app-district-zone-filter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './district-zone-filter.component.html',
  styleUrl: './district-zone-filter.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DistrictZoneFilterComponent),
      multi: true
    }
  ]
})
export class DistrictZoneFilterComponent implements OnInit, ControlValueAccessor {
  isOpen = false;
  disabled = false;
  searchQuery = '';

  districts: DistrictDto[] = [];
  allZones: ZoneOption[] = [];
  groups: DistrictGroup[] = [];

  private selected = new Set<string>();
  private onChange: (value: string[]) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private agentService: AgentService,
    private districtService: DistrictService
  ) { }

  ngOnInit(): void {
    forkJoin({
      districts: this.districtService.getDistricts(),
      zones: this.agentService.getZonesForDistrictFilter()
    }).subscribe({
      next: ({ districts, zones }) => {
        this.districts = districts;
        this.allZones = zones;
        this.buildGroups();
      },
      error: (error) => {
        console.error('Error loading district/zone filter data:', error);
      }
    });
  }

  writeValue(value: string[]): void {
    this.selected = new Set(value ?? []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private buildGroups(): void {
    const groups: DistrictGroup[] = this.districts.map(d => ({
      id: d.id,
      name: d.name,
      zones: this.allZones.filter(z => z.districtId === d.id),
      expanded: false
    }));

    const unassignedZones = this.allZones.filter(
      z => !z.districtId || !this.districts.some(d => d.id === z.districtId)
    );

    if (unassignedZones.length > 0) {
      groups.push({
        id: UNASSIGNED_GROUP_ID,
        name: 'Unassigned',
        zones: unassignedZones,
        expanded: false
      });
    }

    this.groups = groups.filter(g => g.zones.length > 0);
  }

  get isSearching(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  get filteredGroups(): DistrictGroup[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.groups;
    }

    return this.groups
      .map(g => {
        const groupNameMatches = g.name.toLowerCase().includes(query);
        const zones = groupNameMatches
          ? g.zones
          : g.zones.filter(z => z.label.toLowerCase().includes(query));
        return { ...g, zones };
      })
      .filter(g => g.zones.length > 0);
  }

  get filteredZoneCount(): number {
    return this.filteredGroups.reduce((sum, g) => sum + g.zones.length, 0);
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  get triggerLabel(): string {
    if (this.selected.size === 0) {
      return 'All zones';
    }
    if (this.selected.size === 1) {
      const zone = this.allZones.find(z => this.selected.has(z.value));
      return zone?.label ?? '1 zone selected';
    }
    return `${this.selected.size} zones selected`;
  }

  toggleOpen(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.onTouched();
    }
  }

  isGroupExpanded(group: DistrictGroup): boolean {
    return this.isSearching || group.expanded;
  }

  toggleGroupExpanded(group: DistrictGroup): void {
    group.expanded = !group.expanded;
  }

  isZoneSelected(zoneId: string): boolean {
    return this.selected.has(zoneId);
  }

  toggleZone(zoneId: string): void {
    if (this.selected.has(zoneId)) {
      this.selected.delete(zoneId);
    } else {
      this.selected.add(zoneId);
    }
    this.emitChange();
  }

  selectedZoneCount(group: DistrictGroup): number {
    return group.zones.filter(z => this.selected.has(z.value)).length;
  }

  groupSelectionState(group: DistrictGroup): 'checked' | 'unchecked' | 'indeterminate' {
    const zones = this.isSearching ? this.filteredGroups.find(g => g.id === group.id)?.zones ?? [] : group.zones;
    if (zones.length === 0) {
      return 'unchecked';
    }
    const selectedCount = zones.filter(z => this.selected.has(z.value)).length;
    if (selectedCount === 0) {
      return 'unchecked';
    }
    if (selectedCount === zones.length) {
      return 'checked';
    }
    return 'indeterminate';
  }

  toggleGroup(group: DistrictGroup): void {
    const zones = this.isSearching ? this.filteredGroups.find(g => g.id === group.id)?.zones ?? [] : group.zones;
    const state = this.groupSelectionState(group);
    if (state === 'checked') {
      zones.forEach(z => this.selected.delete(z.value));
    } else {
      zones.forEach(z => this.selected.add(z.value));
    }
    this.emitChange();
  }

  get selectAllState(): 'checked' | 'unchecked' | 'indeterminate' {
    const zones = this.filteredGroups.flatMap(g => g.zones);
    if (zones.length === 0) {
      return 'unchecked';
    }
    const selectedCount = zones.filter(z => this.selected.has(z.value)).length;
    if (selectedCount === 0) {
      return 'unchecked';
    }
    if (selectedCount === zones.length) {
      return 'checked';
    }
    return 'indeterminate';
  }

  toggleSelectAll(): void {
    const zones = this.filteredGroups.flatMap(g => g.zones);
    const state = this.selectAllState;
    if (state === 'checked') {
      zones.forEach(z => this.selected.delete(z.value));
    } else {
      zones.forEach(z => this.selected.add(z.value));
    }
    this.emitChange();
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  private emitChange(): void {
    this.onChange(Array.from(this.selected));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.onTouched();
    }
  }
}
